import {
  forwardRef,
  ReactNode,
  FunctionComponent,
  Ref,
  PropsWithoutRef,
  useState,
  useRef,
  KeyboardEvent,
  useContext
} from 'react';
import styled, { css } from 'styled-components';
import { readableColor } from 'polished';

import Button, { ButtonProps } from '../Button';
import Popover from '../Popover';
import BareButton from '../Button/BareButton';
import VisuallyHiddenText from '../VisuallyHiddenText';
import { BaseProps, ForwardProps } from '../../types';
import { defaultThemeProp } from '../../theme';
import { useBreakpoint, useConsolidatedRef, useI18n, useUID } from '../../hooks';
import { tryCatch } from '../../utils';
import { calculateFontSize } from '../../styles';
import { ModalManagerContext } from '../Modal';
import { modalInitializedKey } from '../Modal/Modal.types';
import AppShellContext from '../AppShell/AppShellContext';

export interface LinkProps extends BaseProps {
  /** Text or content for the Link. */
  children: ReactNode;
  /** URL or DOM id to navigate to. */
  href: string;
  /**
   * Controls the styling of the Link.
   * @default "link"
   */
  variant?: ButtonProps['variant'];
  /**
   * Set the Icon prop to `true` if you're using just an Icon in your Link. Make sure to pass an Icon as children if `true`.
   * @default false
   */
  icon?: ButtonProps['icon'];
  /** Determines if the Link should render a popover when focused or hovered over. */
  previewable?: boolean;
  /** Callback function that is run when the preview popover is interacted with. */
  onPreview?: (e: { href: string }) => void;
  /** Specifies where to open the link href. */
  target?: string;
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLAnchorElement>;
}

export const StyledLinkPopover = styled.div`
  background-color: ${props => props.theme.components.tooltip['background-color']};
`;

StyledLinkPopover.defaultProps = defaultThemeProp;

export const StyledLinkPopoverBtn = styled(BareButton)(({ theme }) => {
  const contrastColor = tryCatch(() => readableColor(theme.base.palette.interactive));
  const fontSize = calculateFontSize(theme.base['font-size'], theme.base['font-scale']);

  return css`
    position: relative;
    display: inline-flex;
    align-items: center;
    z-index: 1;
    padding: 0 ${theme.base.spacing};
    min-height: 1.5rem;
    line-height: 1;
    color: ${contrastColor};
    font-size: ${fontSize.xs};
    text-decoration: none;

    /* FIXME: Button selector specificity... */
    & + & {
      margin: 0;
    }

    & + &::before {
      content: '';
      position: absolute;
      inset-inline-start: 0;
      inset-block: 0;
      width: 1px; /* stylelint-disable-line unit-allowed-list */
      background-color: ${contrastColor};
      opacity: ${theme.base.transparency['transparent-3']};
    }

    &:hover,
    &:active {
      text-decoration: underline;
    }

    &:focus {
      box-shadow: inset 0 0 0 0.0625rem ${theme.base.palette.light},
        0 0 0.125rem 0.0625rem ${theme.base.palette.interactive};
    }
  `;
});

StyledLinkPopoverBtn.defaultProps = defaultThemeProp;

export const StyledLink = styled(Button)``;

StyledLink.defaultProps = defaultThemeProp;

const Link: FunctionComponent<LinkProps & ForwardProps> = forwardRef(
  (
    { href, variant = 'link', previewable, onPreview, ...restProps }: PropsWithoutRef<LinkProps>,
    ref: LinkProps['ref']
  ) => {
    const { ModalContext } = useContext(ModalManagerContext);
    const { [modalInitializedKey]: inModal } = useContext(ModalContext);
    const { previewTriggerRef } = useContext(AppShellContext);
    const uid = useUID();
    const linkRef = useConsolidatedRef(ref);
    const [popover, setPopover] = useState(false);
    const previewBtnRef = useRef<HTMLButtonElement>(null);
    const openInTabBtnRef = useRef<HTMLAnchorElement>(null);
    const isSmallOrAbove = useBreakpoint('sm');
    const t = useI18n();

    const openPopover = () => {
      if (isSmallOrAbove) setPopover(true);
    };

    const closePopover = () => {
      setPopover(false);
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (event.keyCode === 27) {
        closePopover();
      }

      if (event.keyCode === 38 || event.keyCode === 40) {
        previewBtnRef?.current?.focus();
      }

      if (event.keyCode === 37 || event.keyCode === 39) {
        if (previewBtnRef.current === document.activeElement) {
          openInTabBtnRef?.current?.focus();
        } else {
          previewBtnRef?.current?.focus();
        }
      }
    };

    const onEnterLink = () => {
      if (previewable) openPopover();
    };

    const onPreviewClick = () => {
      previewTriggerRef.current = linkRef.current;
      onPreview?.({ href });
    };

    return (
      <>
        <StyledLink
          href={href}
          variant={variant}
          ref={linkRef as any}
          {...restProps}
          onMouseEnter={onEnterLink}
          onMouseLeave={closePopover}
          onFocus={onEnterLink}
          onKeyUp={onKeyUp}
          onBlur={closePopover}
          aria-describedby={previewable ? `${uid}-preview-instructions` : undefined}
        />

        <Popover
          show={popover}
          groupId='link_preview'
          showDelay='short'
          hideDelay='short'
          placement='bottom'
          target={linkRef.current}
          onMouseEnter={openPopover}
          onMouseLeave={closePopover}
          as={StyledLinkPopover}
          arrow
        >
          {!inModal && (
            <StyledLinkPopoverBtn
              preview
              type='button'
              ref={previewBtnRef}
              onClick={onPreviewClick}
              tabIndex='-1'
              onFocus={openPopover}
              onBlur={closePopover}
              onKeyUp={onKeyUp}
            >
              {t('preview')}
            </StyledLinkPopoverBtn>
          )}
          <StyledLinkPopoverBtn
            forwardedAs='a'
            href={href}
            ref={openInTabBtnRef}
            target='_blank'
            rel='noreferrer'
            tabIndex='-1'
            onFocus={openPopover}
            onBlur={closePopover}
            onKeyUp={onKeyUp}
          >
            {t('link_open_in_tab_text')}
          </StyledLinkPopoverBtn>
        </Popover>

        {previewable && (
          <VisuallyHiddenText id={`${uid}-preview-instructions`}>
            {t('preview_link_arrow_navigation')}
          </VisuallyHiddenText>
        )}
      </>
    );
  }
);

export default Link;
